import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useApi } from "../../../../hooks/useApi";
import ToggleBtn from "../../../Button/ToggleBtn";
import ConfirmBtn from "../../../Button/ConfirmBtn";
import LineBox from "../../../DefaultStyles/LineBox";
import Flex from "../../../DefaultStyles/Flex";
import Loader from "../../../DefaultStyles/Loader";
import AlertModal from "../../../Modal/AlertModal";
import { Overlay } from "../../../Modal/DefaultModalLayout";
import ManagerPwInputModal from "../../../Modal/ManagerPwInputModal";
import Table, { TableBody, TableBodyItem } from "../../../Table/Table";
import MaintenanceTimeModal from "./ManageServer/MaintenanceTimeModal";
import { OptionType } from "../../../Filters/DropDown";
import LineBtn from "../../../Button/LineBtn";

interface IServer {
  id: number;
  name: number;
  cnt: string;
  today: string;
}

interface ManageServerProps {
  defData?: IServer[];
  refetch: () => void;
}

interface CountModalState {
  visible: boolean;
  id: number;
  name: number;
  currentCnt: number;
  newCnt: number;
}

const INITIAL_COUNT_MODAL: CountModalState = {
  visible: false,
  id: 0,
  name: 0,
  currentCnt: 0,
  newCnt: 0,
};

export default function ManageDefData({ defData, refetch }: ManageServerProps) {
  const [countModal, setCountModal] =
    useState<CountModalState>(INITIAL_COUNT_MODAL);
  const [askModal, setAskModal] = useState({
    visible: false,
    defId: 0,
    newCnt: 0,
  });
  const [pwInputModal, setPwInputModal] = useState(false);

  const [UpdateDef, setUpdateDef] = useState(false); //데이터 업데이트

  const tableHead = ["분류", "오늘 갯수","일일 제한 갯수", "수정하기"];

  // 서버 점검 상태 업데이트 하기
  const { response, error, loading } = useApi({
    method: "post",
    url: UpdateDef ? `version/systemdef` : undefined,
    data: {
      id: askModal?.defId,
      cnt: askModal?.newCnt,
    },
  });

  useEffect(() => {
    if (response?.status === 200 || response?.status === 201) {
      setUpdateDef(false);
      setAskModal({
        ...askModal,
        defId: 0,
        newCnt: 0,
      });
      refetch();
    }
  }, [response]);

  useEffect(() => {
    if (error) {
      toast.error(error?.data ?? "상태를 업데이트 하는 데에 실패하였습니다.");
    }
  }, [error]);

  // useEffect(() => {
  //   setIsMaintenance(defData?.maintenance === "0" ? false : true);
  // }, [defData]);

  useEffect(() => {
    // console.log(UpdateDef, isMaintenance, isChangeTime);
  }, [UpdateDef]);

  const handleCountChange = (value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    setCountModal({ ...countModal, newCnt: num });
  };

  return (
    <>
      <LineBox>
        <Table tableHeadData={tableHead}>
          <>
            {defData?.map((d) => {
              return (
                <TableBody key={d?.id}>
                  <TableBodyItem>{d?.name}</TableBodyItem>
                  <TableBodyItem>{d?.today}</TableBodyItem>
                  <TableBodyItem>{d?.cnt}</TableBodyItem>
                  <TableBodyItem>
                    <LineBtn
                      btnText="수정"
                      btnClickEvent={() => {
                        const currentCnt = Number(d?.cnt) || 0;
                        setCountModal({
                          visible: true,
                          id: d?.id,
                          name: d?.name,
                          currentCnt,
                          newCnt: currentCnt,
                        });
                      }}
                    />
                  </TableBodyItem>
                </TableBody>
              );
            })}
          </>
        </Table>
      </LineBox>

      {loading && <Loader />}

      {/* 갯수 설정 모달 */}
      {countModal.visible && (
        <Overlay id="parent" style={{ zIndex: 30 }}>
          <CountModalContainer>
            <CountModalTitle>일일 제한 갯수 설정</CountModalTitle>
            <CountModalDesc>현재 : {countModal.currentCnt}</CountModalDesc>

            <CountInputRow>
              <CountButton
                onClick={() =>
                  setCountModal({
                    ...countModal,
                    newCnt: Math.max(0, countModal.newCnt - 1),
                  })
                }
              >
                −
              </CountButton>

              <CountInput
                type="number"
                min={0}
                value={countModal.newCnt}
                onChange={(e) => handleCountChange(e.target.value)}
              />

              <CountButton
                onClick={() =>
                  setCountModal({
                    ...countModal,
                    newCnt: countModal.newCnt + 1,
                  })
                }
              >
                +
              </CountButton>
            </CountInputRow>

            <Flex gapSize={8}>
              <>
                <ConfirmBtn
                  btnText="취소"
                  btnColor="white"
                  btnClickEvent={() => setCountModal(INITIAL_COUNT_MODAL)}
                />
                <ConfirmBtn
                  btnText="확인"
                  btnColor="red"
                  btnClickEvent={() => {
                    setCountModal(INITIAL_COUNT_MODAL);
                    setAskModal({
                      visible: true,
                      defId: countModal.id,
                      newCnt: countModal.newCnt,
                    });
                  }}
                />
              </>
            </Flex>
          </CountModalContainer>
        </Overlay>
      )}

      {/* 변경 확인 모달 */}
      {askModal.visible && (
        <AlertModal
          title={`갯수를 ${askModal.newCnt}(으)로 변경합니다.`}
          cancelEvent={() =>
            setAskModal({
              visible: false,
              defId: 0,
              newCnt: 0,
            })
          }
          confirmEvent={() => {
            setPwInputModal(true);
            setAskModal({
              ...askModal,
              visible: false,
            });
          }}
        />
      )}

      {/* 관리자 비밀번호 입력 */}
      {pwInputModal && (
        <ManagerPwInputModal
          closeEvent={() => setPwInputModal(false)}
          confirmEvent={() => {
            setUpdateDef(true);
            setPwInputModal(false);
          }}
          confirmBtnText={"변경하기"}
        />
      )}
    </>
  );
}

/* ---------- Styled Components ---------- */

const CountModalContainer = styled.div`
  max-width: 340px;
  width: 100%;
  border-radius: 14px;
  padding: 28px;
  background-color: ${({ theme }) => theme.color.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  z-index: 30;
`;

const CountModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #222;
  margin: 0;
`;

const CountModalDesc = styled.span`
  font-size: 14px;
  color: #808080;
`;

const CountInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CountButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #dfe3e9;
  background-color: #f6f6f8;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #ebecf0;
  }
`;

const CountInput = styled.input`
  width: 80px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #dfe3e9;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #222;
  outline: none;

  /* Hide number spinner */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    border-color: #4a90d9;
  }
`;
